// The layout of the capability map: the response in, regions, node positions
// and the lines of the pairs out. Pure and deterministic: the same response
// gives the same positions, whatever the render.
//
// - A region is one effect area (nodes without one are in "Unfiled"); the
//   regions sit in rows, ordered by name with "Unfiled" last. The rows are
//   split so that the whole is about as wide as a canvas is, and every row
//   is stretched to the widest, so that no two regions overlap. Given the
//   size of the canvas, the regions are stretched further to fill it.
// - A node takes the room of its dot and of the label under it. Inside a
//   region the nodes start on a grid, are drawn towards the nodes they are
//   paired with, and are kept apart by that room. The start positions come
//   from the node order and the tick count is fixed, so nothing is random.

import { CapabilityMap, CapabilityMapNode } from "../../../types/analytics";

export const UNFILED_ID = "unfiled";
export const UNFILED_LABEL = "Unfiled";

// The least and the most a node's dot measures across, as a radius (px)
export const MIN_RADIUS = 9;
export const MAX_RADIUS = 35;
// The count the largest dot stands for, at the least: a map of small counts
// is not drawn in large dots
const FULL_SIZE_FROM = 20;
// The label under a dot: its width, and the height kept for its two lines
// and the line of counts
export const LABEL_WIDTH = 130;
export const LABEL_HEIGHT = 58;
// The air kept between the rooms of two nodes
const GAP_X = 14;
const GAP_Y = 12;
// The room a region keeps around its nodes, and for its label
const REGION_PADDING = 12;
const REGION_HEADER = 30;
// The width (px) a letter of a region's label takes
const HEADER_LETTER = 7.6;
// How much larger than the rooms of its nodes a region is, and its shape
const REGION_SLACK = 1.5;
const REGION_ASPECT = 1.5;
const REGION_GAP = 20;
// The shape of the whole, as width over height, where the canvas' is unknown
const CANVAS_ASPECT = 1.9;
// How far a region may be stretched to fill the room it is given
const MAX_STRETCH = 2.2;
const TICKS = 200;
const SETTLE_PASSES = 40;

export interface PlacedNode {
  node: CapabilityMapNode;
  // The centre of the dot, in the canvas' coordinates
  x: number;
  y: number;
  radius: number;
  regionId: string;
}

export interface PlacedRegion {
  id: string;
  label: string;
  // The top left corner and the size of the region's surface
  x: number;
  y: number;
  width: number;
  height: number;
  nodeIds: string[];
}

export interface PlacedLine {
  id: string;
  a: string;
  b: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  jaccard: number;
  support: number;
  thin: boolean;
}

export interface CapabilityMapLayout {
  regions: PlacedRegion[];
  nodes: PlacedNode[];
  lines: PlacedLine[];
  width: number;
  height: number;
}

// The radius of a node's dot: it grows with the root of its products, so that
// the area follows the count, up to the largest count on the map
export const radiusOf = (productCount: number, most = FULL_SIZE_FROM): number => {
  const full = Math.max(FULL_SIZE_FROM, most);
  const share = Math.min(1, Math.max(0, productCount) / full);
  return MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * Math.sqrt(share);
};

export interface Footprint {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

// The room a node takes on the canvas: its dot and the label under it
export const footprintOf = (placed: {
  x: number;
  y: number;
  radius: number;
}): Footprint => ({
  left: placed.x - LABEL_WIDTH / 2,
  right: placed.x + LABEL_WIDTH / 2,
  top: placed.y - placed.radius,
  bottom: placed.y + placed.radius + LABEL_HEIGHT,
});

export const regionIdOf = (node: CapabilityMapNode): string =>
  node.effect_area?.id ?? UNFILED_ID;

// The key of a pair is also the id of its line on the canvas, which is
// written into the markup (and into an exported SVG), so it must be text XML
// allows: a space, which no node id holds
export const pairKey = (a: string, b: string): string => `${a} ${b}`;

const round = (value: number): number => Math.round(value * 100) / 100;

// How far a line bows off the straight way, as a share of its length and at
// the most (px)
const BOW_SHARE = 0.14;
const BOW_MOST = 44;

// The way a line takes between two dots: bowed a little, so that a line that
// passes a dot is not read as ending at it, and two lines over one stretch
// are told apart. Every line bows the same way, whichever end is named first.
export const pairPath = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): string => {
  const flipped = x1 > x2 || (x1 === x2 && y1 > y2);
  const [fromX, fromY, toX, toY] = flipped ? [x2, y2, x1, y1] : [x1, y1, x2, y2];
  const length = Math.hypot(toX - fromX, toY - fromY);
  if (length === 0) {
    return `M ${x1} ${y1} L ${x2} ${y2}`;
  }
  const bow = Math.min(BOW_MOST, length * BOW_SHARE);
  const overX = round((fromX + toX) / 2 + ((toY - fromY) / length) * bow);
  const overY = round((fromY + toY) / 2 - ((toX - fromX) / length) * bow);
  return `M ${x1} ${y1} Q ${overX} ${overY} ${x2} ${y2}`;
};

const ROOM_WIDTH = LABEL_WIDTH + GAP_X;

const roomHeight = (radius: number): number =>
  2 * radius + LABEL_HEIGHT + GAP_Y;

interface Room {
  // The centre of the room and half its sides
  x: number;
  y: number;
  halfWidth: number;
  halfHeight: number;
  // Where the room started, to which it is held
  startX: number;
  startY: number;
}

interface Pull {
  a: number;
  b: number;
  strength: number;
}

// The columns and rows of the grid the nodes of a region start on
const gridOf = (count: number, width: number): { columns: number; rows: number } => {
  const fit = Math.max(1, Math.min(count, Math.floor(width / ROOM_WIDTH)));
  const rows = Math.ceil(count / fit);
  return { columns: Math.ceil(count / rows), rows };
};

// The nodes of one region settled inside a box of the given sides, around
// its centre (0, 0); the centres of their dots are returned. The start is a
// grid in node order, each node a little off the centre of its cell.
const settle = (
  radii: number[],
  pulls: Pull[],
  width: number,
  height: number,
): { x: number; y: number }[] => {
  const golden = Math.PI * (3 - Math.sqrt(5));
  const { columns, rows } = gridOf(radii.length, width);
  const cellWidth = width / columns;
  const cellHeight = height / rows;
  const rooms: Room[] = radii.map((radius, index) => {
    const halfHeight = roomHeight(radius) / 2;
    const looseX = Math.max(0, (cellWidth - ROOM_WIDTH) / 2);
    const looseY = Math.max(0, cellHeight / 2 - halfHeight);
    const x =
      -width / 2 +
      cellWidth * ((index % columns) + 0.5) +
      looseX * 0.8 * Math.cos(index * golden);
    const y =
      -height / 2 +
      cellHeight * (Math.floor(index / columns) + 0.5) +
      looseY * 0.8 * Math.sin(index * golden);
    return { x, y, halfWidth: ROOM_WIDTH / 2, halfHeight, startX: x, startY: y };
  });

  // Two rooms that overlap are moved apart along the axis of less overlap,
  // then every room is put back inside the box
  const separate = () => {
    for (let i = 0; i < rooms.length; i += 1) {
      for (let j = i + 1; j < rooms.length; j += 1) {
        const a = rooms[i];
        const b = rooms[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const overX = a.halfWidth + b.halfWidth - Math.abs(dx);
        const overY = a.halfHeight + b.halfHeight - Math.abs(dy);
        if (overX <= 0 || overY <= 0) {
          continue;
        }
        if (overX < overY) {
          const shift = (overX / 2) * (dx < 0 ? -1 : 1);
          a.x -= shift;
          b.x += shift;
        } else {
          const shift = (overY / 2) * (dy < 0 ? -1 : 1);
          a.y -= shift;
          b.y += shift;
        }
      }
    }
    for (const room of rooms) {
      const limitX = Math.max(0, width / 2 - room.halfWidth);
      const limitY = Math.max(0, height / 2 - room.halfHeight);
      room.x = Math.max(-limitX, Math.min(limitX, room.x));
      room.y = Math.max(-limitY, Math.min(limitY, room.y));
    }
  };

  for (let tick = 0; tick < TICKS; tick += 1) {
    const heat = 1 - tick / TICKS;
    for (const room of rooms) {
      room.x += (room.startX - room.x) * 0.05 * heat;
      room.y += (room.startY - room.y) * 0.05 * heat;
    }
    for (const pull of pulls) {
      const a = rooms[pull.a];
      const b = rooms[pull.b];
      const stepX = (b.x - a.x) * 0.06 * pull.strength * heat;
      const stepY = (b.y - a.y) * 0.06 * pull.strength * heat;
      a.x += stepX;
      a.y += stepY;
      b.x -= stepX;
      b.y -= stepY;
    }
    separate();
  }
  for (let pass = 0; pass < SETTLE_PASSES; pass += 1) {
    separate();
  }

  // The dot sits at the top of its room, under half the air
  return rooms.map((room, index) => ({
    x: room.x,
    y: room.y - room.halfHeight + GAP_Y / 2 + radii[index],
  }));
};

interface RegionBox {
  id: string;
  label: string;
  nodes: CapabilityMapNode[];
  radii: number[];
  width: number;
  height: number;
}

// The rows the boxes are split into, in order: a box is in the row its middle
// falls into when the widths are laid end to end and cut into equal parts
const rowsOf = (boxes: RegionBox[], count: number): RegionBox[][] => {
  const total = boxes.reduce((sum, box) => sum + box.width, 0);
  const rows: RegionBox[][] = Array.from({ length: count }, () => []);
  let cursor = 0;
  for (const box of boxes) {
    const middle = cursor + box.width / 2;
    const row = Math.min(count - 1, Math.floor((middle / total) * count));
    rows[row].push(box);
    cursor += box.width;
  }
  return rows.filter((row) => row.length > 0);
};

const rowWidth = (row: RegionBox[]): number =>
  row.reduce((sum, box) => sum + box.width, 0) + REGION_GAP * (row.length - 1);

const rowHeight = (row: RegionBox[]): number =>
  Math.max(...row.map((box) => box.height));

const rowsHeight = (rows: RegionBox[][]): number =>
  rows.reduce((sum, row) => sum + rowHeight(row), 0) +
  REGION_GAP * (rows.length - 1);

// The split whose shape is the nearest to the canvas'
const packRows = (boxes: RegionBox[], aspect: number): RegionBox[][] => {
  let best: RegionBox[][] = [];
  let bestScore = Infinity;
  for (let count = 1; count <= boxes.length; count += 1) {
    const rows = rowsOf(boxes, count);
    const width = Math.max(...rows.map(rowWidth));
    const score = Math.abs(Math.log(width / rowsHeight(rows) / aspect));
    if (score < bestScore) {
      best = rows;
      bestScore = score;
    }
  }
  return best;
};

// The room the map is laid out for: the canvas, in px at a zoom of 1
export interface LayoutRoom {
  width: number;
  height: number;
}

// With a room, the regions are stretched to fill it, up to MAX_STRETCH times
// their own size, so that a map does not sit small in the middle of a large
// canvas; without one they keep their own size.
export const layoutCapabilityMap = (
  map: CapabilityMap,
  room: LayoutRoom | null = null,
): CapabilityMapLayout => {
  const groups = new Map<string, { label: string; nodes: CapabilityMapNode[] }>();
  for (const node of map.nodes) {
    const id = regionIdOf(node);
    const group = groups.get(id) ?? {
      label: node.effect_area?.name ?? node.effect_area?.id ?? UNFILED_LABEL,
      nodes: [],
    };
    group.nodes.push(node);
    groups.set(id, group);
  }
  const order = Array.from(groups.entries()).sort(([idA, a], [idB, b]) => {
    if (idA === UNFILED_ID || idB === UNFILED_ID) {
      return idA === UNFILED_ID ? (idB === UNFILED_ID ? 0 : 1) : -1;
    }
    return a.label.localeCompare(b.label) || idA.localeCompare(idB);
  });

  const most = Math.max(0, ...map.nodes.map((node) => node.product_count));
  const boxes: RegionBox[] = order.map(([id, group]) => {
    const radii = group.nodes.map((node) => radiusOf(node.product_count, most));
    const heights = radii.map(roomHeight);
    const area =
      heights.reduce((sum, height) => sum + ROOM_WIDTH * height, 0) * REGION_SLACK;
    const inner = Math.max(
      ROOM_WIDTH,
      Math.sqrt(area * REGION_ASPECT),
      group.label.length * HEADER_LETTER,
    );
    // Tall enough for the rows of the grid the nodes start on
    const tallest = Math.max(...heights);
    const { rows } = gridOf(radii.length, inner);
    return {
      id,
      label: group.label,
      nodes: group.nodes,
      radii,
      width: Math.ceil(inner + 2 * REGION_PADDING),
      height: Math.ceil(
        Math.max(area / inner, rows * tallest) + REGION_HEADER + REGION_PADDING,
      ),
    };
  });

  const aspect = room
    ? Math.max(1, Math.min(3, room.width / room.height))
    : CANVAS_ASPECT;
  const rows = boxes.length > 0 ? packRows(boxes, aspect) : [];
  const ownWidth = rows.length > 0 ? Math.max(...rows.map(rowWidth)) : 0;
  const ownHeight = rows.length > 0 ? rowsHeight(rows) : 0;
  const filled = (own: number, given: number | undefined): number =>
    given === undefined
      ? own
      : Math.min(own * MAX_STRETCH, Math.max(own, Math.floor(given)));
  const canvasWidth = filled(ownWidth, room?.width);
  const canvasHeight = filled(ownHeight, room?.height);
  // What the room has over in height goes to the rows, by their height
  const gaps = REGION_GAP * Math.max(0, rows.length - 1);
  const taller = ownHeight > gaps ? (canvasHeight - gaps) / (ownHeight - gaps) : 1;

  const regions: PlacedRegion[] = [];
  const nodes: PlacedNode[] = [];
  let cursorY = 0;
  for (const row of rows) {
    const height = round(rowHeight(row) * taller);
    // Every row is as wide as the widest: what is over goes to its regions
    const stretch = (canvasWidth - rowWidth(row)) / row.reduce((sum, box) => sum + box.width, 0);
    let cursorX = 0;
    for (const box of row) {
      const width = round(box.width * (1 + stretch));
      regions.push({
        id: box.id,
        label: box.label,
        x: round(cursorX),
        y: cursorY,
        width,
        height,
        nodeIds: box.nodes.map((node) => node.id),
      });
      // The pairs inside the region draw their nodes together
      const local = new Map(box.nodes.map((node, index) => [node.id, index]));
      const pulls: Pull[] = [];
      for (const pair of map.pairs) {
        const a = local.get(pair.a);
        const b = local.get(pair.b);
        if (a !== undefined && b !== undefined && a !== b) {
          pulls.push({ a, b, strength: Math.max(0, Math.min(1, pair.jaccard)) });
        }
      }
      const innerWidth = width - 2 * REGION_PADDING;
      const innerHeight = height - REGION_HEADER - REGION_PADDING;
      const settled = settle(box.radii, pulls, innerWidth, innerHeight);
      const centreX = cursorX + width / 2;
      const centreY = cursorY + REGION_HEADER + innerHeight / 2;
      box.nodes.forEach((node, index) => {
        nodes.push({
          node,
          x: round(centreX + settled[index].x),
          y: round(centreY + settled[index].y),
          radius: box.radii[index],
          regionId: box.id,
        });
      });
      cursorX += width + REGION_GAP;
    }
    cursorY += height + REGION_GAP;
  }

  const placed = new Map(nodes.map((n) => [n.node.id, n]));
  const lines: PlacedLine[] = [];
  for (const pair of map.pairs) {
    const a = placed.get(pair.a);
    const b = placed.get(pair.b);
    if (!a || !b) {
      continue;
    }
    lines.push({
      id: pairKey(pair.a, pair.b),
      a: pair.a,
      b: pair.b,
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y,
      jaccard: pair.jaccard,
      support: pair.support,
      thin: pair.thin,
    });
  }

  return {
    regions,
    nodes,
    lines,
    width: canvasWidth,
    height: Math.max(0, cursorY - REGION_GAP),
  };
};
